import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import setPref from '@/redux/actions/uiActions/setPref';
import selectors from '@/redux/selectors/selectors';
import { useCallback, useLayoutEffect, useRef } from 'react';

const WorkflowLogsColumnResizer = ({ label, widthProperty, minWidth, minFollowingWidth, contentMinSelector, preferenceKey }) => {
  const persistedWidth = useArgSelector(selectors.pref, preferenceKey);
  const resizerRef = useRef(null);
  const resizeStart = useRef(null);
  const preferredWidth = useRef(null);

  const getMinWidth = useCallback(
    (column) => {
      const content = contentMinSelector ? [...column.querySelectorAll(contentMinSelector)] : [];
      const row = content[0]?.closest('.log-entry');
      const marker = row?.querySelector('.status-marker');

      if (!row || !marker) {
        return minWidth;
      }

      const columnStyle = getComputedStyle(column);
      const rowStyle = getComputedStyle(row);
      const markerStyle = getComputedStyle(marker);
      const statusStyle = getComputedStyle(marker.parentElement);
      const spacing = [columnStyle.paddingLeft, columnStyle.paddingRight, rowStyle.paddingLeft, rowStyle.paddingRight, rowStyle.columnGap, markerStyle.marginLeft, markerStyle.marginRight, statusStyle.columnGap].reduce((total, value) => total + (parseFloat(value) || 0), marker.offsetWidth);
      return Math.max(minWidth, Math.ceil(Math.max(...content.map((element) => element.scrollWidth)) + spacing));
    },
    [contentMinSelector, minWidth]
  );

  const setWidth = useCallback(
    (resizer, width, effectiveMinWidth = getMinWidth(resizer.previousElementSibling)) => {
      const column = resizer.previousElementSibling;
      const parentRect = resizer.parentElement.getBoundingClientRect();
      const columnRect = column.getBoundingClientRect();
      const maxWidth = Math.max(effectiveMinWidth, parentRect.right - columnRect.left - minFollowingWidth);
      const nextWidth = Math.min(maxWidth, Math.max(effectiveMinWidth, width));
      column.style.setProperty(widthProperty, `${nextWidth}px`);
      resizer.setAttribute('aria-valuemin', Math.ceil(effectiveMinWidth).toString());
      resizer.setAttribute('aria-valuemax', Math.floor(maxWidth).toString());
      resizer.setAttribute('aria-valuenow', Math.round(nextWidth).toString());
      return nextWidth;
    },
    [getMinWidth, minFollowingWidth, widthProperty]
  );

  const persistWidth = (width) => {
    preferredWidth.current = width;
    if (width !== parseFloat(persistedWidth)) {
      setPref(preferenceKey, width.toString());
    }
  };

  useLayoutEffect(() => {
    const resizer = resizerRef.current;
    const restoredWidth = parseFloat(persistedWidth);
    const initialWidth = Number.isFinite(restoredWidth) ? restoredWidth : resizer.previousElementSibling.getBoundingClientRect().width;
    if (initialWidth > 0) {
      preferredWidth.current = initialWidth;
      setWidth(resizer, initialWidth);
    }

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (preferredWidth.current !== null) {
        setWidth(resizer, preferredWidth.current);
      }
    });
    resizeObserver.observe(resizer.parentElement);
    return () => resizeObserver.disconnect();
  }, [persistedWidth, setWidth]);

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const column = event.currentTarget.previousElementSibling;
    resizeStart.current = { x: event.clientX, width: column.getBoundingClientRect().width, minWidth: getMinWidth(column) };
  };

  const handlePointerMove = (event) => {
    if (resizeStart.current) {
      preferredWidth.current = setWidth(event.currentTarget, resizeStart.current.width + event.clientX - resizeStart.current.x, resizeStart.current.minWidth);
    }
  };

  const handlePointerUp = (event) => {
    if (resizeStart.current) {
      persistWidth(preferredWidth.current || resizeStart.current.width);
    }
    resizeStart.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    persistWidth(setWidth(event.currentTarget, event.currentTarget.previousElementSibling.getBoundingClientRect().width + direction * 10));
  };

  return <div ref={resizerRef} className="workflow-logs-column-resizer" role="separator" aria-label={label} aria-orientation="vertical" aria-valuemin={minWidth} aria-valuenow={minWidth} tabIndex={0} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp} onKeyDown={handleKeyDown} />;
};

export default WorkflowLogsColumnResizer;
